import { useState } from "react";
import { Loader2, MapPin, ShieldCheck, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const SECTIONS = [
  ["dados", "Meus dados", UserCog],
  ["endereco", "Endereço", MapPin],
  ["senha", "Senha", ShieldCheck],
];

export function ProfileModal({ me, onClose, onSaved }) {
  const [section, setSection] = useState("dados");
  const [form, setForm] = useState({
    name: me.name || "",
    email: me.email || "",
    contact: me.contact || "",
    document: me.document || "",
    notes: me.notes || "",
    zip_code: me.zip_code || "",
    address: me.address || "",
    address_number: me.address_number || "",
    complement: me.complement || "",
    district: me.district || "",
    city: me.city || "",
    state: me.state || "",
  });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setP = (k) => (e) => setPwd((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/partner/profile", form);
      toast.success("Seus dados foram atualizados.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) {
      toast.error("A nova senha precisa ter pelo menos 6 caracteres.");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("A confirmação não confere com a nova senha.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Senha alterada. Use a nova senha no próximo acesso.");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Meu perfil"
      description="Seus dados de contato, endereço e senha de acesso. Preencha só o que fizer sentido para você."
      testid="partner-profile-modal"
    >
      <div className="mt-2 space-y-4">
        <div className="flex flex-wrap gap-2">
          {SECTIONS.map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSection(key)}
              data-testid={`profile-section-${key}`}
              className={`tr-chip gap-1.5 ${section === key ? "tr-chip-active" : ""}`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {section === "dados" && (
          <form onSubmit={saveProfile} className="flex flex-col gap-4">
            <Field label="Nome / Loja">
              <Input value={form.name} onChange={set("name")} required minLength={2} data-testid="profile-name-input" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="E-mail de acesso" hint="É o e-mail usado para entrar no sistema.">
                <Input
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="profile-email-input"
                />
              </Field>
              <Field label="WhatsApp / telefone">
                <Input
                  value={form.contact}
                  onChange={set("contact")}
                  className="font-num"
                  data-testid="profile-contact-input"
                />
              </Field>
            </div>
            <Field label="CPF ou CNPJ" hint="Opcional">
              <Input
                value={form.document}
                onChange={set("document")}
                className="font-num"
                data-testid="profile-document-input"
              />
            </Field>
            <Field label="Observações">
              <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="profile-notes-input" />
            </Field>
            <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="profile-cancel-btn">
                Cancelar
              </Button>
              <Button type="submit" disabled={busy} className="gap-2 rounded-full" data-testid="profile-save-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
              </Button>
            </div>
          </form>
        )}

        {section === "endereco" && (
          <form onSubmit={saveProfile} className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="CEP">
                <Input value={form.zip_code} onChange={set("zip_code")} className="font-num" data-testid="profile-zip-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Endereço">
                  <Input value={form.address} onChange={set("address")} data-testid="profile-address-input" />
                </Field>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Número">
                <Input value={form.address_number} onChange={set("address_number")} className="font-num" data-testid="profile-number-input" />
              </Field>
              <Field label="Complemento">
                <Input value={form.complement} onChange={set("complement")} data-testid="profile-complement-input" />
              </Field>
              <Field label="Bairro">
                <Input value={form.district} onChange={set("district")} data-testid="profile-district-input" />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Field label="Cidade">
                  <Input value={form.city} onChange={set("city")} data-testid="profile-city-input" />
                </Field>
              </div>
              <Field label="Estado / UF">
                <Input
                  value={form.state}
                  onChange={set("state")}
                  maxLength={2}
                  className="uppercase"
                  data-testid="profile-state-input"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="profile-address-cancel-btn">
                Cancelar
              </Button>
              <Button type="submit" disabled={busy} className="gap-2 rounded-full" data-testid="profile-address-save-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
              </Button>
            </div>
          </form>
        )}

        {section === "senha" && (
          <form onSubmit={savePassword} className="flex flex-col gap-4">
            <p className="rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground">
              Por segurança, a senha atual nunca é exibida. Informe a senha atual para definir uma nova.
            </p>
            <Field label="Senha atual">
              <Input
                type="password"
                value={pwd.current}
                onChange={setP("current")}
                required
                autoComplete="current-password"
                data-testid="profile-current-password"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nova senha" hint="Mínimo de 6 caracteres">
                <Input
                  type="password"
                  value={pwd.next}
                  onChange={setP("next")}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="profile-new-password"
                />
              </Field>
              <Field label="Confirmar nova senha">
                <Input
                  type="password"
                  value={pwd.confirm}
                  onChange={setP("confirm")}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="profile-confirm-password"
                />
              </Field>
            </div>
            <div className="flex justify-end border-t border-white/10 pt-4">
              <Button type="submit" disabled={busy} className="gap-2 rounded-full" data-testid="profile-password-save-btn">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Alterar senha
              </Button>
            </div>
          </form>
        )}
      </div>
    </ResponsiveModal>
  );
}
